import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ResourceItem } from "@/data/content";
import { trackEvent } from "@/utils/analytics";

/** What the cart shows; the server re-reads prices from Supabase at checkout. */
export type CartLine = {
  id: string;
  title: string;
  priceCents: number;
  currency: string;
  coverSrc?: string;
  type: string;
};

type CartContextValue = {
  lines: CartLine[];
  count: number;
  totalCents: number;
  currency: string;
  has: (id: string) => boolean;
  add: (item: ResourceItem) => void;
  remove: (id: string) => void;
  clear: () => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const STORAGE_KEY = "lucentia-cart-v1";

const CartContext = createContext<CartContextValue | null>(null);

function readStored(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((line): line is CartLine => typeof line?.id === "string" && Number(line?.priceCents) > 0)
      : [];
  } catch {
    return [];
  }
}

/**
 * Cart for paid resources. Digital goods, so each resource is in it at most
 * once. Lives in localStorage; it is empty while prerendering and hydrating.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setLines(readStored());
    setLoaded(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLines(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* Private mode or storage full: the cart still works for this visit. */
    }
  }, [lines, loaded]);

  const add = useCallback((item: ResourceItem) => {
    if (!item.id || item.priceCents <= 0) return;
    const id = item.id;
    setLines((current) =>
      current.some((line) => line.id === id)
        ? current
        : [
            ...current,
            {
              id,
              title: item.title,
              priceCents: item.priceCents,
              currency: item.currency,
              coverSrc: item.coverSrc,
              type: item.type,
            },
          ],
    );
    trackEvent("cart_add", { resource: item.title });
    setIsOpen(true);
  }, []);

  const remove = useCallback((id: string) => {
    setLines((current) => current.filter((line) => line.id !== id));
  }, []);

  const clear = useCallback(() => setLines([]), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      count: lines.length,
      totalCents: lines.reduce((sum, line) => sum + line.priceCents, 0),
      currency: lines[0]?.currency ?? "eur",
      has: (id: string) => lines.some((line) => line.id === id),
      add,
      remove,
      clear,
      isOpen,
      open,
      close,
    }),
    [lines, add, remove, clear, isOpen, open, close],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside <CartProvider>");
  return value;
}
